import { model as router } from '!/router'
import { aye, exists, nay, random, T } from '@/helpers'
import { logger } from '@/logger'
import { equalTo, maxLength, minLength, password, required } from '@/validators'
import { api } from '@setplex/tria-api'
import {
  attach,
  createEffect,
  createEvent,
  createStore,
  sample,
  type Store,
} from 'effector'
import { createForm } from 'effector-forms'
import { debounce } from 'patronum/debounce'
import { model as signInModel } from '~/features/subscriber/sign-in'
import type { ViewError } from '~/features/subscriber/sign-up'
import { humanizeTo, untoken } from '../../shared/tools'

// init page
export const init = createEvent()

// parse data from location hash
export const parsed = createEvent<{
  id: string // registration uuid to confirm code
  from: number // `id` request time
  to: number // end of life of `id`
}>()

// get data from location state
export const stated = createEvent<{ email?: string; code: string }>()

// set new password effect
export const setNewPasswordFx = attach({
  effect: api.subscriber.setNewPasswordFx,
})

// sign in request effect
export const signInFx = attach({ effect: signInModel.signInFx })

// effect to create hash token
const untokenFx = createEffect<
  string,
  { id: string; from: number; to: number }
>((token) => untoken(token))

// effect for waiting
const timeoutFx = createEffect<number, void>(
  (time) => new Promise((resolve) => setTimeout(resolve, time))
)

// reset password session uuid
export const $id = createStore<string | null>(null) //
  .on(parsed, (__, { id }) => id)

// reset password session uuid request time
export const $from = createStore<number | null>(null) //
  .on(parsed, (__, { from }) => from)

// reset password session uuid end of life
export const $to = createStore<number | null>(null) //
  .on(parsed, (__, { to }) => to)

// reset password session email
export const $email = createStore<string | null>(null) //
  .on(stated, (__, { email }) => email ?? null)

// reset password session code
export const $code = createStore<string | null>(null) //
  .on(stated, (__, { code }) => code)

// reset password session password
export const $password = createStore<string | null>(null) //
  .on(setNewPasswordFx.done, (__, { params }) => params.password)

// error to show to the user
export const $error = createStore<string | null>(null)

// debounced error, in order to prevent react to render component with old data
export const $viewError = createStore<string | null>(null)

// lock interface after successful password reset
export const $locked = createStore<boolean>(false)
  .reset(init)
  .on(setNewPasswordFx.done, T)

// new password form
export const newPasswordForm = createForm({
  fields: {
    password: {
      init: '' as string,
      rules: [required(), maxLength(64), minLength(6), password()],
    },
    confirm: {
      init: '' as string,
      rules: [required(), equalTo('password')],
    },
  },
  validateOn: ['submit', 'blur'],
})

// send reset password request
sample({
  clock: newPasswordForm.formValidated,
  source: { id: $id, code: $code },
  filter: ({ id, code }) => exists(id) && exists(code),
  fn: ({ id, code }, { password }) => ({ id: id!, code: code!, password }),
  target: setNewPasswordFx,
})

// reset form on init route
sample({
  clock: init,
  target: newPasswordForm.reset,
})

// decode hash token to get `id` and `id`s end of life
sample({
  clock: init,
  source: router.$hash,
  filter: Boolean,
  fn: (hash) => hash.replace(/^#?/, ''),
  target: untokenFx,
})

// in case there is no hash -> redirect back to /forgot-password
sample({
  clock: init,
  source: router.$hash,
  filter: (hash) => nay(exists(hash)),
  fn: () => ({ pathname: '/forgot-password' }),
  target: router.navigateReplace,
})

// in case of failed to decode hash -> redirect back to /forgot-password
sample({
  clock: untokenFx.fail,
  fn: () => ({ pathname: '/forgot-password' }),
  target: router.navigateReplace,
})

// in case we could not decode token -> log error
sample({
  clock: untokenFx.fail,
  fn: (fail) => ['could not decode token:', fail],
  target: logger.errorFx,
})

// get `id` and `id`s end of life
sample({
  clock: untokenFx.doneData,
  target: parsed,
})

// get `email` and `code` from history state
// this will get preserved in browser, but if user shared his verify link -> state would be empty
sample({
  clock: init,
  source: router.$state as Store<{ email?: string; code: string } | null>,
  filter: (state) => exists(state) && exists(state.code),
  fn: (state) => ({ email: state!.email, code: state!.code }),
  target: stated,
})

// in case there is no `code` -> redirect back to /forgot-password
// we cannot reset password without code
sample({
  clock: init,
  source: router.$state as Store<{ email?: string; code: string } | null>,
  filter: (state) => nay(exists(state)) || nay(exists(state?.code)),
  fn: () => ({ pathname: '/forgot-password' }),
  target: router.navigateReplace,
})

//
// try to autologin after successful password change
//

// in case of successful password change, if we lost email — just show success message
sample({
  clock: setNewPasswordFx.done,
  source: { email: $email, password: $password },
  filter: ({ email, password }) => nay(email) || nay(password),
  fn: () => ({ pathname: '/forgot-password/done' }),
  target: router.navigatePush,
})

// in case of successful password change, if we HAVE email and password — wait a bit and try to login
sample({
  clock: setNewPasswordFx.done,
  source: { email: $email, password: $password },
  filter: ({ email, password }) => aye(email) && aye(password),
  fn: () => random(2000, 3000), // between 2 and 3 seconds
  target: timeoutFx,
})

// lost email or password after timeout??? WAT? show success message
sample({
  clock: timeoutFx.done,
  source: { email: $email, password: $password },
  filter: ({ email, password }) => nay(email) || nay(password),
  fn: () => ({ pathname: '/forgot-password/done' }),
  target: router.navigatePush,
})

// still has email and password -> try to autologin
sample({
  clock: timeoutFx.done,
  source: { email: $email, password: $password },
  filter: ({ email, password }) => aye(email) && aye(password),
  fn: ({ email, password }) => ({ username: email!, password: password! }),
  target: signInFx,
})

// successfully authorized -> go to Home
sample({
  clock: signInFx.done,
  fn: () => ({ pathname: '/' }),
  target: router.navigatePush,
})

const onSignInSubscriptionExpired = createEvent<ViewError>()
// failed to authorize -> retry shortly
sample({
  clock: signInFx.failData,
  // ingore re request when subscription is expired
  filter: (data) => data?.payload?.errorCode !== 'WG0009',
  fn: (data) => {
    // FIXME: hardcoded code
    if (data?.payload?.errorCode !== 'WG0018') {
      return random(1000, 2000) // between 1 and 2 seconds,
    }

    const { remainsLockSeconds } = data?.payload?.payload ?? {} // get `remainsLockSeconds` value
    if (typeof remainsLockSeconds === 'number') {
      return remainsLockSeconds * 1000 // maybe it would be too long?
    }

    return random(3000, 4000) // or between 3 and 4 seconds
  },
  target: timeoutFx,
})

sample({
  clock: signInFx.failData,
  filter: (data) => data?.payload?.errorCode === 'WG0009',
  fn: () => 'subscription_expired_error',
  target: onSignInSubscriptionExpired,
})

debounce({
  source: onSignInSubscriptionExpired,
  timeout: 50,
  target: signInModel.$viewError,
})
sample({
  clock: onSignInSubscriptionExpired,
  fn: () => ({ pathname: '/sign-in' }),
  target: router.navigatePush,
})

// TODO: add timer to limit auth tries, maybe 30 seconds?

//
// handle dynamic fields errors (~ server side validation)
//

// WG0034 - Password is not valid
sample({
  clock: api.events.logic.passwordNotValid,
  fn: () => ({ rule: 'invalid' }),
  target: newPasswordForm.fields.password.addError,
})

//
// Permanent errors -> redirect to /fail page
//

sample({
  clock: api.events.logic.resetPasswordCodeSessionExpired,
  fn: () => ({
    pathname: '/forgot-password/fail',
    state: 'reset_password_request_expired_error',
  }),
  target: router.navigateReplace,
})

sample({
  clock: parsed,
  filter: ({ to }) => humanizeTo(to) === null,
  fn: () => ({
    pathname: '/forgot-password/fail',
    state: 'reset_password_request_expired_error',
  }),
  target: router.navigateReplace,
})

sample({
  clock: api.events.logic.sessionInvalid,
  fn: () => ({
    pathname: '/forgot-password/fail',
    state: 'unknown_error',
  }),
  target: router.navigateReplace,
})

//
// add listeners for server errors
//

$error
  .reset(
    newPasswordForm.$values.updates,
    init,

    // "server validation" errors
    api.events.logic.passwordNotValid,
    api.events.logic.accountIpACLLock // handled by geoblock navigation sub-process
  )
  .on(
    api.events.logic.sessionInvalid, // WG0044 - Session is invalid
    () => 'session_invalid_error'
  )
  .on(
    api.events.logic.resetPasswordCodeInvalid, // WG0045 - Reset password code is invalid
    () => 'invalid_sms_code_error'
  )
  .on(
    api.events.logic.resetPasswordCodeSessionExpired, // WG0046 - Reset password session has expired
    () => 'reset_password_request_expired_error'
  )
  .on(
    api.events.logic.noMoreConfirmationAttempts, // WG0047 - No more confirmation attempts are allowed
    () => 'confirm_fail_attempt_error'
  )

  // some generic request errors
  .on(
    api.events.http.httpError, // any non-200 error
    (error) => (error === null ? 'unknown_error' : error)
  )
  .on(
    api.events.http.error, // any request error (dns, carrier, etc.)
    (error) => (error === null ? 'request_failed_error' : error)
  )

debounce({
  source: $error,
  timeout: 50,
  target: $viewError,
})

$viewError.reset(newPasswordForm.$values.updates, init)
